import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

/**
 * Theme store — three modes:
 *   - "system" (default) follows the OS-level prefers-color-scheme
 *   - "light" / "dark" explicitly override
 *
 * The user's pick is persisted in localStorage. Effective theme is
 * computed from (mode, system) and applied via `data-theme` attribute
 * on `<html>`. Components can either react to the `data-theme`
 * selector at CSS level, or to `useThemeStore().effective` at the
 * Vue level.
 */
export type ThemePreference = "system" | "light" | "dark";
export type EffectiveTheme = "light" | "dark";

const STORAGE_KEY = "web1-crm:theme";

function readPersisted(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "system" || v === "light" || v === "dark") return v;
  } catch {
    /* SSR / privacy mode — fall through */
  }
  return "system";
}

function detectSystem(): EffectiveTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export const useThemeStore = defineStore("theme", () => {
  const mode = ref<ThemePreference>(readPersisted());
  const system = ref<EffectiveTheme>(detectSystem());

  const effective = computed<EffectiveTheme>(() =>
    mode.value === "system" ? system.value : mode.value,
  );

  // Subscribe to OS-level preference changes — covers macOS auto
  // light/dark transitions at sunrise/sunset and Windows nightlight.
  if (typeof window !== "undefined" && window.matchMedia) {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent): void => {
      system.value = e.matches ? "dark" : "light";
    };
    if ("addEventListener" in mq) {
      mq.addEventListener("change", onChange);
    } else {
      // Safari < 14 fallback
      (mq as MediaQueryList & {
        addListener: (cb: (e: MediaQueryListEvent) => void) => void;
      }).addListener(onChange);
    }
  }

  // Apply data-theme + persist whenever the effective theme changes.
  watch(
    effective,
    (next) => {
      if (typeof document !== "undefined") {
        document.documentElement.setAttribute("data-theme", next);
      }
    },
    { immediate: true },
  );

  watch(mode, (next) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  });

  /** Cycle system → light → dark → system. */
  function cycle(): void {
    mode.value =
      mode.value === "system"
        ? "light"
        : mode.value === "light"
          ? "dark"
          : "system";
  }

  function setMode(next: ThemePreference): void {
    mode.value = next;
  }

  return { mode, system, effective, cycle, setMode };
});
